import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Cpu, Vault, Network, ShieldCheck, Database, TerminalSquare, ArrowRight, ArrowUpRight } from "lucide-react";
import { MODULES, PIPELINE, HOME_AGENTS, ROADMAP } from "../../data";
import { ASSETS } from "../../config";
import { SectionHeader, Reveal, Chip, Label, StatusDot, Panel } from "../primitives";
import { DataStatus } from "../DataStatus";
import { usePolling } from "../../lib/api";
import { PortfolioBrain } from "../PortfolioBrain";

const ICONS = { Cpu, Vault, Network, ShieldCheck, Database, TerminalSquare };

export const Modules = () => (
  <section className="border-t border-white/[0.06] bg-ink-850 py-20 md:py-28" data-testid="modules-section">
    <div className="mx-auto max-w-[1440px] px-5 md:px-8">
      <SectionHeader
        eyebrow="Product Modules"
        title="ONE LAYER. SIX SYSTEMS."
        desc="Every module is a composable primitive in the autonomous finance stack — from orchestration to execution."
      />
      <div className="grid gap-px border border-white/[0.08] bg-white/[0.08] sm:grid-cols-2 lg:grid-cols-3">
        {MODULES.map((m, i) => {
          const Icon = ICONS[m.icon];
          return (
            <Reveal key={m.n} delay={i * 0.06}>
              <div
                className="group relative h-full bg-panel p-6 transition-colors duration-300 hover:bg-elevated"
                data-testid={`module-card-${m.n}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-fog-600">{m.n}</span>
                  <Chip tone={m.status === "LIVE CONCEPT" ? "green" : "gold"}>{m.status}</Chip>
                </div>
                <Icon className="mt-8 h-6 w-6 text-fog-600 transition-colors duration-300 group-hover:text-pulse-soft" strokeWidth={1.5} />
                <h3 className="mt-4 font-display text-base font-bold tracking-wide text-fog-100">{m.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fog-400">{m.desc}</p>
                <span className="absolute inset-x-0 bottom-0 h-[2px] origin-left scale-x-0 bg-gradient-to-r from-pulse to-brass transition-transform duration-300 group-hover:scale-x-100" />
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const HowItWorks = () => (
  <section className="border-t border-white/[0.06] py-20 md:py-28" data-testid="pipeline-section">
    <div className="mx-auto max-w-[1440px] px-5 md:px-8">
      <SectionHeader
        eyebrow="How It Works"
        title="MANDATE TO EXECUTION"
        desc="A four-stage neural pipeline. Nothing reaches capital without passing through every stage."
      />
      <div className="relative grid gap-6 md:grid-cols-4 md:gap-0">
        <div className="absolute left-0 right-0 top-6 hidden h-px bg-white/[0.08] md:block" />
        {PIPELINE.map((p, i) => (
          <Reveal key={p.n} delay={i * 0.1} className="relative">
            <div className="relative md:pr-8" data-testid={`pipeline-step-${p.n}`}>
              <span className={`relative z-10 flex h-12 w-12 items-center justify-center border font-mono text-xs tracking-[0.15em] ${
                i === 3 ? "border-brass/50 bg-brass/10 text-brass-bright" : "border-pulse/40 bg-panel text-pulse-soft"}`}>
                {p.n}
              </span>
              <h3 className="mt-5 font-display text-lg font-bold tracking-wide text-fog-100">{p.title}</h3>
              <p className="mt-2 max-w-[240px] text-sm leading-relaxed text-fog-400">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const AgentPreview = () => (
  <section className="border-t border-white/[0.06] bg-ink-850 py-20 md:py-28" data-testid="agent-preview-section">
    <div className="mx-auto max-w-[1440px] px-5 md:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeader
          eyebrow="Agent Ecosystem"
          title="SPECIALIZED INTELLIGENCE"
          desc="Conceptual demo agents from the SYNAPTIX registry. No performance claims — protocol concepts only."
        />
        <Reveal>
          <Link to="/agents" data-testid="view-all-agents"
            className="group mb-12 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-pulse-soft md:mb-16">
            Full Registry <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="grid gap-px border border-white/[0.08] bg-white/[0.08] sm:grid-cols-2 lg:grid-cols-3">
        {HOME_AGENTS.map((a, i) => (
          <Reveal key={a.id} delay={i * 0.05}>
            <div className="group h-full bg-panel p-5 transition-colors duration-300 hover:bg-elevated" data-testid={`home-agent-${a.id.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-bold tracking-[0.12em] text-fog-100">{a.id}</span>
                <Chip tone="muted">Not Deployed</Chip>
              </div>
              <div className="mt-5 space-y-1.5">
                {[["Role", a.role], ["Risk", a.risk], ["Specialization", a.spec]].map(([k, v]) => (
                  <div key={k} className="flex justify-between font-mono text-[11px]">
                    <span className="uppercase tracking-[0.18em] text-fog-600">{k}</span>
                    <span className="text-fog-100">{v}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between font-mono text-[11px]">
                  <span className="uppercase tracking-[0.18em] text-fog-600">Status</span>
                  <span className={`flex items-center gap-1.5 ${a.status === "ACTIVE" ? "text-pulse-soft" : "text-fog-400"}`}>
                    <StatusDot tone={a.status === "ACTIVE" ? "green" : "muted"} /> {a.status}
                  </span>
                </div>
              </div>
              <Link to="/agents" data-testid={`home-agent-view-${a.id.toLowerCase()}`}
                className="mt-5 flex items-center gap-2 border-t border-white/[0.06] pt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-fog-600 transition-colors group-hover:text-pulse-soft">
                View Agent <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const BrainSection = () => (
  <section className="border-t border-white/[0.06] py-20 md:py-28" data-testid="brain-section">
    <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 md:px-8 lg:grid-cols-2">
      <SectionHeader
        eyebrow="Portfolio Intelligence"
        title="THE PORTFOLIO BRAIN"
        desc="A persistent intelligence layer that will read real portfolio state, concentration and regime — and explain what it sees in plain language. It activates when agent services are deployed; no simulated metrics are shown."
      />
      <Reveal delay={0.15}>
        <PortfolioBrain />
      </Reveal>
    </div>
  </section>
);

export const Roadmap = () => (
  <section className="border-t border-white/[0.06] bg-ink-850 py-20 md:py-28" data-testid="roadmap-section">
    <div className="mx-auto max-w-[1440px] px-5 md:px-8">
      <SectionHeader
        eyebrow="Roadmap"
        title="PATH TO AUTONOMY"
        desc="Four phases. No fake dates — only engineering states."
      />
      <div className="grid gap-px border border-white/[0.08] bg-white/[0.08] md:grid-cols-2 xl:grid-cols-4">
        {ROADMAP.map((r, i) => (
          <Reveal key={r.phase} delay={i * 0.08}>
            <div className="h-full bg-panel p-6" data-testid={`roadmap-${r.phase.replace(" ", "-").toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-[0.25em] text-fog-600">{r.phase}</span>
                <Chip tone={r.status === "IN DEVELOPMENT" ? "green" : r.status === "PLANNED" ? "gold" : "muted"}>{r.status}</Chip>
              </div>
              <h3 className="mt-6 font-display text-xl font-bold tracking-wide text-fog-100">{r.title}</h3>
              <ul className="mt-4 space-y-2">
                {r.items.map((it) => (
                  <li key={it} className="flex items-center gap-2.5 text-sm text-fog-400">
                    <span className="h-1 w-1 rotate-45 bg-brass/70" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const FinalCTA = () => (
  <section className="relative overflow-hidden border-t border-white/[0.06]" data-testid="final-cta">
    <img src={ASSETS.ban} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
    <div className="absolute inset-0 bg-ink-900/82" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/60 to-transparent" />
    <div className="relative mx-auto max-w-[1440px] px-5 py-28 md:px-8 md:py-40">
      <Reveal>
        <Label tone="gold" className="mb-5">The Transition Has Started</Label>
        <h2 className="max-w-3xl font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-fog-100 sm:text-5xl lg:text-6xl">
          CAPITAL IS BECOMING <span className="text-pulse-soft">AUTONOMOUS.</span>
        </h2>
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-fog-400 sm:text-base">
          SYNAPTIX provides the intelligence layer between human intent and onchain execution.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/terminal" data-testid="cta-enter-terminal"
            className="group flex items-center gap-2.5 bg-pulse px-7 py-4 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ink-900 transition-all hover:bg-pulse-soft hover:shadow-[0_0_36px_rgba(0,200,5,0.4)]">
            Enter Terminal <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/agents" data-testid="cta-explore-agents"
            className="border border-white/20 bg-ink-900/60 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-fog-100 backdrop-blur-sm transition-all hover:border-brass/60 hover:text-brass-bright">
            Explore Agents
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export const MetricsBar = () => {
  const { data, error } = usePolling("/chain/status", 10000);
  const items = [
    ["System", error ? "Offline" : data ? "Online" : "…", !error && !!data],
    ["Network", "Robinhood Chain", false],
    ["Block", data?.blockNumber ? `#${data.blockNumber.toLocaleString()}` : "Indexing…", !!data?.blockNumber],
    ["Indexer", data ? (data.syncStatus === "synchronized" ? "Synchronized" : data.syncStatus) : "…", data?.syncStatus === "synchronized"],
    ["Vault Engine", "Standby", false],
    ["Execution", "Coming Soon", false],
  ];
  return (
    <div className="border-y border-white/[0.06] bg-ink-850" data-testid="metrics-bar">
      <div className="mx-auto grid max-w-[1440px] grid-cols-2 divide-white/[0.06] px-5 sm:grid-cols-3 md:px-8 lg:grid-cols-6 lg:divide-x">
        {items.map(([k, v, dot]) => (
          <div key={k} className="flex flex-col gap-1 py-4 lg:px-5 lg:first:pl-0">
            <Label>{k}</Label>
            <span className={`flex items-center gap-1.5 font-mono text-xs ${v === "Coming Soon" ? "text-brass-bright" : "text-fog-100"}`}>
              {dot && <StatusDot />} {v}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
